import React, { useState, useEffect } from 'react';
import './App.css';
import MapView from './components/MapView';
import ConnectionStatus from './components/ConnectionStatus';
import RaceSelector from './components/RaceSelector';
import { WebSocketProvider, useWebSocket } from './contexts/WebSocketContext';

interface SelectedRace {
  id: string;
  name: string;
  center: { lat: number; lng: number };
}

function AppContent({ selectedRace }: { selectedRace: SelectedRace }) {
  const [delayedTime, setDelayedTime] = useState(new Date(new Date().getTime() - 60000)); // 60 seconds behind
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [minimumLoadingComplete, setMinimumLoadingComplete] = useState(false);
  const [loadingMessage] = useState(() => {
    const messages = [
      "Calibrating the altimeters...",
      "Checking wind conditions...",
      "Inflating the paragliders...",
      "Syncing with GPS satellites...",
      "Brewing coffee for the pilots...",
      "Untangling reserve parachutes...",
      "Counting clouds for thermal detection...",
      "Teaching eagles to share thermals...",
      "Downloading flight plans from the clouds...",
      "Waking up sleeping pilots...",
      "Negotiating with the wind gods...",
      "Polishing the variometers...",
      "Charging the GPS batteries...",
      "Studying thermal dynamics...",
      "Adjusting harness straps..."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  });
  const { tasks, raceMetadata, isConnected, raceNotFound } = useWebSocket();

  // Update clock every second - MUST be before any conditional returns
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setDelayedTime(new Date(now.getTime() - 60000)); // 60 seconds behind
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Ensure minimum 3 seconds loading time for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumLoadingComplete(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Show loading screen while waiting for race metadata OR minimum time
  // But skip if race not found
  if (!raceNotFound && (!isConnected || !raceMetadata || !minimumLoadingComplete)) {

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f8f9fa',
        color: '#2c3e50'
      }}>
        <img
          src="/hfss_logo.png"
          alt="HFSS Logo"
          style={{
            width: '120px',
            height: 'auto',
            marginBottom: '40px',
            animation: 'float 3s ease-in-out infinite'
          }}
        />

        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '30px'
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#667eea',
            animation: 'bounce 1.4s ease-in-out infinite'
          }}></div>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#764ba2',
            animation: 'bounce 1.4s ease-in-out 0.2s infinite'
          }}></div>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#667eea',
            animation: 'bounce 1.4s ease-in-out 0.4s infinite'
          }}></div>
        </div>

        <h2 style={{
          margin: '0 0 15px 0',
          fontSize: '28px',
          fontWeight: '300',
          letterSpacing: '1px'
        }}>
          Connecting to Race
        </h2>

        <p style={{
          margin: '0',
          fontSize: '15px',
          opacity: 0.7,
          fontStyle: 'italic',
          animation: 'fadeIn 1s ease-in'
        }}>
          {loadingMessage}
        </p>

        <style dangerouslySetInnerHTML={{__html: `
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }

          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1.2);
              opacity: 1;
            }
          }

          @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 0.7; transform: translateY(0); }
          }
        `}} />
      </div>
    );
  }

  // Use race name from metadata if available
  const raceName = raceMetadata?.race_name || selectedRace.name;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <div className="header-logo">
            <a href="https://www.hikeandfly.app" target="_blank" rel="noopener noreferrer">
              <img src="/hfss_logo.png" alt="HFSS" className="header-logo-img" />
            </a>
          </div>
          <div className="header-divider"></div>
          <div className="header-race-info">
            <h1>{raceName}</h1>
            {raceMetadata?.location && (
              <span className="header-location">{raceMetadata.location}</span>
            )}
          </div>
        </div>
        <div className="header-controls">
          <div className="task-controls">
            <select 
              className="task-selector"
              value={selectedTask}
              onChange={(e) => setSelectedTask(e.target.value)}
              title="Select Task"
            >
              <option value="">No Task</option>
              {tasks.map((task, index) => {
                const taskName = task.task_name || `Task ${index + 1}`;
                const taskKey = task.task_name || `task-${index}`;
                return (
                  <option key={taskKey} value={taskName}>
                    {taskName}
                  </option>
                );
              })}
            </select>
          </div>
          <ConnectionStatus />
        </div>
      </header>
      <main className="app-main">
        <MapView
          raceCenter={selectedRace.center}
          raceName={selectedRace.name}
          raceId={selectedRace.id}
          selectedTask={tasks.find(t => t.task_name === selectedTask)}
          showTask={true}
          delayedTime={delayedTime}
        />
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <span className="copyright">© {new Date().getFullYear()} Simone Severini. All rights reserved.</span>
          <div className="footer-links">
            <a href="https://www.hikeandfly.app/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            <span className="separator">•</span>
            <a href="https://www.hikeandfly.app/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  const [selectedRace, setSelectedRace] = useState<SelectedRace | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const raceId = urlParams.get('race');

    if (raceId) {
      // When race ID is in URL, use it with default name (will be updated from race_config)
      return {
        id: raceId,
        name: 'Loading...',
        center: { lat: 47.3769, lng: 8.5417 } // Default center
      };
    }

    // No race ID in URL - show selector
    return null;
  });

  const handleRaceSelect = (raceId: string) => {
    // Set the selected race with minimal info - full details will come from race_config
    setSelectedRace({
      id: raceId,
      name: 'Loading...',
      center: { lat: 47.3769, lng: 8.5417 } // Default center
    });
  };

  return (
    <div style={{ position: 'relative', height: '100vh' }}>
      {selectedRace ? (
        <WebSocketProvider raceId={selectedRace.id}>
          <AppContent selectedRace={selectedRace} />
        </WebSocketProvider>
      ) : (
        // Show default background when no race selected
        <div style={{
          height: '100vh',
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: 'white',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: '300'
          }}>
            Loading Live Tracking...
          </div>
        </div>
      )}

      {/* Modal overlay when no race is selected */}
      {!selectedRace && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000
        }}>
          <RaceSelector onRaceSelect={handleRaceSelect} />
        </div>
      )}
    </div>
  );
}

export default App;